import {type FocusEvent, type SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState} from 'react'

export function useGutterFocus() {
  const scopeRef = useRef<HTMLDivElement | null>(null)
  const pointerTargetRef = useRef<Element | null>(null)
  const focusedElementRef = useRef<Element | null>(null)
  const [focusedLine, setFocusedLine] = useState<number | null>(null)
  const [request, setRequest] = useState<{line: number} | null>(null)
  const ref = useCallback((node: HTMLDivElement) => {
    scopeRef.current = node
    return () => {
      scopeRef.current = null
      pointerTargetRef.current = null
      focusedElementRef.current = null
    }
  }, [])

  useEffect(() => {
    const clearPointer = () => {
      pointerTargetRef.current = null
    }
    window.addEventListener('pointerup', clearPointer, true)
    window.addEventListener('mouseup', clearPointer, true)
    window.addEventListener('pointercancel', clearPointer, true)
    window.addEventListener('blur', clearPointer)
    return () => {
      window.removeEventListener('pointerup', clearPointer, true)
      window.removeEventListener('mouseup', clearPointer, true)
      window.removeEventListener('pointercancel', clearPointer, true)
      window.removeEventListener('blur', clearPointer)
    }
  }, [])

  const rememberPointerTarget = useCallback((event: SyntheticEvent<HTMLDivElement>) => {
    if (event.target instanceof Element && event.currentTarget.contains(event.target)) {
      pointerTargetRef.current = event.target.closest('[data-gutter-line]')
    }
  }, [])
  const onBlurCapture = useCallback((event: FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.target)) return
    if (event.relatedTarget || document.hasFocus() || event.target !== document.activeElement) {
      focusedElementRef.current = null
    }
  }, [])
  const onFocusCapture = useCallback((event: FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.target)) return
    const repeatedFocus = focusedElementRef.current === event.target
    focusedElementRef.current = event.target
    const row = event.target.closest<HTMLElement>('[data-gutter-line]')
    // Pointer focus must not move a click target; window reactivation is not a new reveal.
    if (repeatedFocus || (row && row === pointerTargetRef.current)) return
    const line = Number(row?.getAttribute('data-gutter-line'))
    if (Number.isInteger(line) && line > 0) setRequest({line})
  }, [])
  const onFocusChange = useCallback((line: number | null) => {
    if (line === null && !document.hasFocus() && scopeRef.current?.contains(document.activeElement)) return
    setFocusedLine(line)
  }, [])
  const handlers = useMemo(
    () => ({
      onPointerDownCapture: rememberPointerTarget,
      // Touch compatibility mousedown can focus a target after pointerup.
      onMouseDownCapture: rememberPointerTarget,
      onBlurCapture,
      onFocusCapture,
    }),
    [rememberPointerTarget, onBlurCapture, onFocusCapture],
  )

  const focus = useMemo(() => ({focusedLine, request, onFocusChange}), [focusedLine, request, onFocusChange])
  return {ref, focus, handlers}
}

export type GutterFocus = ReturnType<typeof useGutterFocus>['focus']
